/**
 * AttackDragOverlay - Visual feedback for touch drag attacks
 * 
 * Shows:
 * - A line from the attacker to the current finger position
 * - Target highlighting when hovering over valid targets
 * - Attacker indicator
 */

import React from 'react';
import { createPortal } from 'react-dom';
import './AttackDragOverlay.css';

const AttackDragOverlay = ({
  isDragging,
  attackerInfo,
  startPosition,
  dragPosition,
  hoveredTarget,
}) => {
  if (!isDragging || !attackerInfo) {
    return null;
  }
  
  // Calculate line angle for arrow direction
  const deltaX = dragPosition.x - startPosition.x;
  const deltaY = dragPosition.y - startPosition.y;
  const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
  const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  
  // Determine if we have a valid target
  const hasValidTarget = hoveredTarget !== null;
  const isGeneralTarget = hoveredTarget?.type === 'general';
  const isUnitTarget = hoveredTarget?.type === 'unit';
  
  return createPortal(
    <div className="attack-drag-overlay">
      {/* Attack line from attacker to finger */}
      <svg className="attack-line-svg" style={{ pointerEvents: 'none' }}>
        <defs>
          {/* Gradient for the attack line */}
          <linearGradient id="attackLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ff4444" stopOpacity="0.8" />
            <stop offset="100%" stopColor={hasValidTarget ? '#ff0000' : '#ff8888'} stopOpacity="1" />
          </linearGradient>
          
          {/* Glow filter */}
          <filter id="attackGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Main attack line */}
        <line
          x1={startPosition.x}
          y1={startPosition.y}
          x2={dragPosition.x}
          y2={dragPosition.y}
          stroke="url(#attackLineGradient)"
          strokeWidth={hasValidTarget ? 6 : 4}
          strokeLinecap="round"
          filter="url(#attackGlow)"
          className={hasValidTarget ? 'valid-target' : ''}
        />
        
        {/* Animated dashes along the line */}
        <line
          x1={startPosition.x}
          y1={startPosition.y}
          x2={dragPosition.x}
          y2={dragPosition.y}
          stroke="#ffffff"
          strokeWidth="2"
          strokeDasharray="10,10"
          strokeLinecap="round"
          className="attack-line-dashes"
        />
      </svg>
      
      {/* Attacker indicator (start point) - simple glow */}
      <div 
        className="attack-start-indicator"
        style={{
          left: startPosition.x,
          top: startPosition.y,
        }}
      />
      
      {/* Target indicator (end point) - simple glow */}
      <div 
        className={`attack-target-indicator ${hasValidTarget ? 'valid' : 'invalid'} ${isGeneralTarget ? 'general' : ''}`}
        style={{
          left: dragPosition.x,
          top: dragPosition.y,
        }}
      />
    </div>,
    document.body
  );
};

export default AttackDragOverlay;

